import { createContext, useContext, useState, useCallback, useRef } from 'react';
import useAuth from './useAuth';

const API_URL = process.env.REACT_APP_API_URL || "https://ukd45lve3kvj45tahklem6d67q0fbbxm.lambda-url.us-west-2.on.aws/api/chat";

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
    const auth = useAuth();
    const [messages, setMessages] = useState([]);
    const [busy, setBusy] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsError, setSuggestionsError] = useState(false);
    const lastActivityRef = useRef(Date.now());
    const sendSeqRef = useRef(0);
    // true = next send should signal a new session to the backend.
    // Set to false when session messages are restored via seedMessages (page refresh).
    // Reset to true on signOut (via reset()).
    const isNewSessionRef = useRef(true);

    const send = useCallback(async (text, images = null) => {
        text = (text || '').trim();
        if (!text && (!images || images.length === 0)) return;
        if (busy) return;

        lastActivityRef.current = Date.now();
        const seq = ++sendSeqRef.current;

        // Build previews for display
        const userMsg = { role: "user", content: text };
        if (images && images.length > 0) {
            userMsg.images = images.map(img => ({
                preview: `data:${img.media_type};base64,${img.data}`,
            }));
        }
        setMessages(prev => [...prev, userMsg]);
        setBusy(true);

        try {
            const headers = { "Content-Type": "application/json" };
            const token = await auth?.getIdToken();
            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }

            const payload = { message: text };
            if (images && images.length > 0) {
                payload.images = images;
            }
            if (isNewSessionRef.current) {
                payload.new_session = true;
                isNewSessionRef.current = false;
            }

            const res = await fetch(API_URL, {
                method: "POST",
                headers,
                body: JSON.stringify(payload),
            });

            if (res.status === 429) {
                setMessages(prev => [...prev, { role: "error", content: "Rate limit reached. Try again in a bit." }]);
            } else if (!res.ok) {
                let errText = "Please try again.";
                try {
                    const j = await res.json();
                    if (j.error) errText = j.error;
                } catch (_) {}
                setMessages(prev => [...prev, { role: "error", content: "Something went wrong. " + errText }]);
            } else {
                const data = await res.json();
                const reply = data.reply || "(no reply)";
                const assistantMsg = { role: "assistant", content: reply };
                if (data.proposed_action) {
                    assistantMsg.proposedAction = data.proposed_action;
                    assistantMsg.estimatedMinutes = data.estimated_minutes || null;
                }
                if (data.sources && data.sources.length > 0) {
                    assistantMsg.sources = data.sources;
                }
                if (data.context_update) {
                    assistantMsg.contextUpdate = data.context_update;
                }
                if (data.field_report_logged) {
                    assistantMsg.fieldReportLogged = true;
                }
                setMessages(prev => [...prev, assistantMsg]);
                // Only apply suggestion state if this response is for the latest send
                if (seq === sendSeqRef.current) {
                    setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
                    setSuggestionsError(!!data.suggestions_error);
                }
            }
        } catch (err) {
            setMessages(prev => [...prev, { role: "error", content: "Couldn't reach the server. Check your connection." }]);
            if (seq === sendSeqRef.current) {
                setSuggestions([]);
            }
        } finally {
            setBusy(false);
        }
    }, [busy, auth]);

    const reset = useCallback(() => {
        setMessages([]);
        setSuggestions([]);
        setSuggestionsError(false);
        isNewSessionRef.current = true;
    }, []);

    const seedMessages = useCallback((initialMessages) => {
        if (!Array.isArray(initialMessages) || initialMessages.length === 0) return;
        // Only seed if the client doesn't already have a conversation in flight
        setMessages(prev => prev.length === 0 ? initialMessages : prev);
        // Restoring a session — the next send should continue it, not clear it
        isNewSessionRef.current = false;
    }, []);

    return (
        <ChatContext.Provider value={{
            messages, busy, send, reset, seedMessages,
            suggestions, setSuggestions,
            suggestionsError, setSuggestionsError,
        }}>
            {children}
        </ChatContext.Provider>
    );
}

export default function useChat() {
    return useContext(ChatContext);
}
